import React from 'react';
import classNames from 'classnames';
import { useHistory } from 'react-router-dom';

import { BarCode, Reload } from '../../../components/custom-icons';

import { Card, getLastBalanceAddedDate } from 'common/api/e-comm/models/Card';
import { ProductType } from 'common/api/e-comm/models/Product';
import { useRootSelector } from 'common/features/featuresReducer';
import { selectBrandId } from 'common/features/store/duck/brands/duck';
import { monetizeWithDecimal } from 'common/utils/strings';
import { createStyles } from 'utils/createStyle';
import { formatDateObject } from 'common/utils/time';
import { Routes } from 'routes';
import { useSpring, animated } from 'react-spring';
import { ShopOnlineWalletCardMarker } from './ShopOnlineWalletCardMarker';
import { WalletCardAd } from './WalletCardAd/WalletCardAd';
import { useCardReloadability } from '../utils/reloadable';

interface WalletCardProps {
    idx: number;
    card: Card;
    archived: boolean;
    openUnArchiveModal?: React.Dispatch<React.SetStateAction<string | null>>;
    shouldShowAd?: boolean;
}

interface IFadeIn {
    classes?: string;
}

export const FadeIn = ({ children, classes = '' }: React.PropsWithChildren<IFadeIn>) => {
    const styles = useSpring({
        from: { opacity: 0 },
        to: { opacity: 1 },
        delay: 300,
    });

    return (
        <animated.div className={classes} style={{ ...styles }}>
            {children}
        </animated.div>
    );
};

const styles = createStyles({
    card: [
        'border-solid border-grey-3 border my-3 md:flex md:flex-col md:justify-between md:items-center',
        { borderRadius: 10 },
    ],
    doubleDipCard: ['border-brand'],
    brandImage: ['mr-8 object-contain', { width: 40, '@media (min-width: 500px)': { width: 80 } }],
    cardTypeIcon: ['object-contain text-grey-1 mr-1', { height: 15 }],
    balanceUpdated: ['text-grey-1 font-semibold', { fontSize: 10, lineHeight: '15px' }],
    lastViewed: ['text-brand font-semibold', { fontSize: 10, lineHeight: '15px' }],
    reloadablePill: [
        'text-grey-1 font-semibold bg-grey-5 rounded-full ml-2 px-2 normal-case',
        { fontSize: 10, lineHeight: '15px', paddingTop: 1, paddingBottom: 1 },
    ],
});

type CardTypeIcon = (props: React.ComponentProps<'svg'>) => JSX.Element;

function generateCardMetaData(card: Card): [string, CardTypeIcon] {
    if (card.type === ProductType.EGiftCard || card.type === ProductType.DigitalReload) {
        return ['eGift', BarCode];
    }

    return ['Physical', Reload];
}

const WalletCard = ({ card, archived, openUnArchiveModal, shouldShowAd }: WalletCardProps) => {
    const brands = useRootSelector((s) => selectBrandId(s));
    const history = useHistory();
    const cardBrand = brands && brands[card.brandId];
    const cardName = card.name || cardBrand?.name;
    const [cardType, CardTypeIcon] = generateCardMetaData(card);
    const isReloadable = useCardReloadability()(card).isReloadable;

    const redirectToDetailPage = () =>
        history.push(Routes.WalletMyCardDetails.replace(':cardId', card.id));
    const balanceUpdatedDate = formatDateObject(getLastBalanceAddedDate(card));
    const unArchiveCard = () => {
        openUnArchiveModal && openUnArchiveModal(card.id);
    };

    const onClick = archived ? unArchiveCard : redirectToDetailPage;

    const lastViewedDate = card.lastViewedDate
        ? new Date(card.lastViewedDate).toLocaleDateString('en-US')
        : '--/--/----';

    return (
        <React.Fragment>
            <article className={classNames(styles.card)}>
                <ShopOnlineWalletCardMarker brandId={card.brandId} />
                <div className="w-full px-6 py-5 md:flex md:flex-row md:px-5 md:py-5 md:justify-between md:items-center">
                    <section className="flex">
                        <FadeIn>
                            <img className={styles.brandImage} src={cardBrand?.imageUrl} />
                        </FadeIn>
                        <div className="flex flex-col">
                            <h4
                                style={{ maxWidth: 280 }}
                                className="text-brand-dark font-semibold text-2xl mb-1 md:mr-14 overflow-ellipsis whitespace-nowrap overflow-hidden"
                            >
                                {cardName}
                            </h4>
                            <label className="text-grey-1 text-xs flex items-center mb-1 font-semibold">
                                <CardTypeIcon className={styles.cardTypeIcon} />
                                {cardType}
                                {isReloadable && (
                                    <span className={styles.reloadablePill}>Reloadable</span>
                                )}
                            </label>
                        </div>
                    </section>
                    <section className="flex justify-between mt-7 md:mt-0">
                        <div
                            className={classNames(
                                'flex-col items-center justify-end md:justify-start md:mr-12',
                                archived ? 'hidden' : 'flex'
                            )}
                        >
                            <h5 className="text-brand text-2xl mb-1">
                                {monetizeWithDecimal(card.cardBalance || 0)}
                            </h5>
                            <small className={styles.balanceUpdated}>
                                As of {balanceUpdatedDate}
                            </small>
                        </div>
                        <div className="flex flex-col items-center justify-end w-36">
                            <button
                                onClick={onClick}
                                className="text-white no-underline cursor-pointer text-lg rounded-full border-2 border-brand px-8 py-1 mb-1 md:px-10 bg-brand"
                            >
                                {archived ? 'Unarchive' : 'View'}
                            </button>
                            <span className="text-brand pt-2 text-xs text-grey-medium w-36 -mr-3">
                                Last Viewed: {lastViewedDate}
                            </span>
                        </div>
                    </section>
                </div>

                {cardBrand && shouldShowAd && (
                    <WalletCardAd cardBrand={cardBrand} location="wallet_view" />
                )}
            </article>
        </React.Fragment>
    );
};

export default WalletCard;
